/**
 * TaskScheduleTab — FullCalendar timeGridWeek for task scheduling context.
 *
 * Shows the selected task and its sibling tasks (same workflow instance) on a
 * weekly calendar grid. Derives event times from task timing fields:
 * - startedAt/completedAt for actual task windows
 * - createdAt + estimatedDurationMs for projected tasks
 * - Current task highlighted with accent ring
 *
 * Only visible for: task primitive type (via CONTEXTUAL_TABS).
 *
 * DUMB FRONTEND: All data from entity fields, no hardcoded assumptions.
 * Technology: FullCalendar Standard (MIT — FREE)
 */

import { useMemo, useRef } from 'react';
import {
  Stack,
  Text,
  Center,
  Group,
  Badge,
  Paper,
  SegmentedControl,
  Box,
} from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconCircleCheck,
  IconPlayerPlay,
  IconAlertTriangle,
} from '@tabler/icons-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventContentArg, EventInput } from '@fullcalendar/core';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import { GqlTaskStatus } from '@/generated/graphql';
import {
  getTaskStatusColor,
  DEFAULT_STATUS_COLOR,
  TASK_STATUS_LEGEND,
} from '@/pages/workflow-state/task-status-colors';

// Default duration when no estimate is available (1 hour)
const DEFAULT_DURATION_MS = 3_600_000;

// ============================================================================
// HELPERS
// ============================================================================

interface TaskLikeEntity {
  id?: string;
  name?: string;
  status?: string;
  priority?: string;
  createdAt?: string;
  startedAt?: string;
  completedAt?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  dueAt?: string;
  estimatedDurationMs?: string;
  durationMs?: string;
  sequenceOrder?: number;
  assignedActor?: { id: string; displayName: string } | null;
  workflowInstanceId?: string;
}

function toTaskLike(entity: PrimitiveItem): TaskLikeEntity {
  return entity as unknown as TaskLikeEntity;
}

/**
 * Get event time range with scheduling-first priority (DRR-116):
 *   1. scheduledStart/scheduledEnd (planned schedule)
 *   2. startedAt/completedAt (actual execution)
 *   3. startedAt + estimatedDurationMs (in-progress)
 *   4. createdAt + estimatedDurationMs (last resort)
 */
function getEventTimeRange(task: TaskLikeEntity): { start: Date; end: Date } {
  const estimatedMs = task.estimatedDurationMs
    ? parseInt(task.estimatedDurationMs, 10)
    : DEFAULT_DURATION_MS;

  // Scheduled tasks: use planned schedule (preferred)
  if (task.scheduledStart && task.scheduledEnd) {
    return {
      start: new Date(task.scheduledStart),
      end: new Date(task.scheduledEnd),
    };
  }

  if (task.scheduledStart) {
    const start = new Date(task.scheduledStart);
    return {
      start,
      end: new Date(start.getTime() + estimatedMs),
    };
  }

  // Completed tasks: use startedAt→completedAt if both exist
  if (task.startedAt && task.completedAt) {
    return {
      start: new Date(task.startedAt),
      end: new Date(task.completedAt),
    };
  }

  // In-progress tasks: startedAt → startedAt + estimated
  if (task.startedAt) {
    const start = new Date(task.startedAt);
    return {
      start,
      end: new Date(start.getTime() + estimatedMs),
    };
  }

  // Pending/future tasks: createdAt → createdAt + estimated
  if (task.createdAt) {
    const start = new Date(task.createdAt);
    return {
      start,
      end: new Date(start.getTime() + estimatedMs),
    };
  }

  // Fallback: now → now + 1hr
  const now = new Date();
  return { start: now, end: new Date(now.getTime() + DEFAULT_DURATION_MS) };
}

// ============================================================================
// COMPONENT
// ============================================================================

interface TaskScheduleTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
  /** Sibling tasks from the same workflow (optional, for multi-task view) */
  siblingTasks?: PrimitiveItem[];
}

export function TaskScheduleTab({ entity, primitiveType, siblingTasks }: TaskScheduleTabProps) {
  const calendarRef = useRef<FullCalendar>(null);

  const currentTask = useMemo(() => entity ? toTaskLike(entity) : null, [entity]);

  // Build calendar events from the current task + siblings
  const calendarEvents = useMemo<EventInput[]>(() => {
    if (!currentTask) return [];
    const events: EventInput[] = [];
    const tasksToShow = siblingTasks
      ? siblingTasks.map(toTaskLike)
      : [currentTask];

    // If no siblings provided, just show the current task
    for (const task of tasksToShow) {
      const { start, end } = getEventTimeRange(task);
      const colors = getTaskStatusColor(task.status);
      const isCurrent = task.id === currentTask.id;

      events.push({
        id: task.id ?? 'unknown',
        title: task.name ?? 'Unnamed Task',
        start: start.toISOString(),
        end: end.toISOString(),
        backgroundColor: isCurrent ? colors.bg : `${colors.bg}99`,
        borderColor: isCurrent ? '#1971c2' : colors.border,
        textColor: '#fff',
        extendedProps: {
          status: task.status,
          priority: task.priority,
          assignedActorId: task.assignedActor?.id,
          sequenceOrder: task.sequenceOrder,
          isCurrent,
        },
      });

      // DRR-116: Add deadline marker if dueAt is set
      if (task.dueAt) {
        events.push({
          id: `${task.id ?? 'unknown'}-deadline`,
          title: `\u23F0 ${task.name ?? 'Task'} — deadline`,
          start: task.dueAt,
          allDay: false,
          display: 'background',
          backgroundColor: 'rgba(201, 42, 42, 0.15)',
          borderColor: '#c92a2a',
        });
      }
    }

    // Add milestone markers for key timestamps
    if (currentTask.createdAt) {
      events.push({
        id: `${currentTask.id}-created`,
        title: 'Created',
        start: currentTask.createdAt,
        allDay: false,
        display: 'background',
        backgroundColor: 'rgba(34,139,230,0.1)',
      });
    }

    return events;
  }, [entity, siblingTasks, currentTask]);

  // Determine initial date — center on the task's most relevant timestamp (DRR-116)
  const initialDate = useMemo(() => {
    if (!currentTask) return new Date();
    if (currentTask.scheduledStart) return new Date(currentTask.scheduledStart);
    if (currentTask.startedAt) return new Date(currentTask.startedAt);
    if (currentTask.createdAt) return new Date(currentTask.createdAt);
    return new Date();
  }, [entity]);

  // Summary stats
  const stats = useMemo(() => {
    if (!currentTask) return { total: 0, completed: 0, inProgress: 0, pending: 0 };
    const tasks = siblingTasks ? siblingTasks.map(toTaskLike) : [currentTask];
    const completed = tasks.filter(t => t.status === GqlTaskStatus.COMPLETED).length;
    const inProgress = tasks.filter(t => t.status === GqlTaskStatus.IN_PROGRESS).length;
    const pending = tasks.filter(t =>
      t.status === GqlTaskStatus.PENDING || t.status === GqlTaskStatus.READY
    ).length;
    return { total: tasks.length, completed, inProgress, pending };
  }, [entity, siblingTasks, currentTask]);

  if (!entity) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select a task to view its schedule.</Text>
      </Center>
    );
  }

  return (
    <Stack gap="sm" py="xs">
      {/* Stats bar */}
      <Group gap="xs" px="xs">
        <Badge
          variant="light"
          color="green"
          size="sm"
          leftSection={<IconCircleCheck size={12} />}
        >
          {stats.completed} done
        </Badge>
        <Badge
          variant="light"
          color="blue"
          size="sm"
          leftSection={<IconPlayerPlay size={12} />}
        >
          {stats.inProgress} active
        </Badge>
        <Badge
          variant="light"
          color="yellow"
          size="sm"
          leftSection={<IconClock size={12} />}
        >
          {stats.pending} pending
        </Badge>
        <Badge variant="light" color="gray" size="sm">
          {stats.total} total
        </Badge>
      </Group>

      {/* Calendar */}
      <Paper
        shadow="xs"
        radius="sm"
        withBorder
        p="xs"
        style={{ minHeight: 420 }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          initialDate={initialDate}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridWeek,timeGridDay,listWeek',
          }}
          events={calendarEvents}
          editable={false}
          selectable={false}
          dayMaxEvents={true}
          weekends={true}
          height={400}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={false}
          nowIndicator={true}
          slotDuration="00:30:00"
          expandRows={true}
          eventContent={renderTaskEvent}
          eventDisplay="block"
          dayHeaderFormat={{ weekday: 'short', month: 'numeric', day: 'numeric' }}
        />
      </Paper>

      {/* Legend — derived from shared TASK_STATUS_LEGEND */}
      <Group gap="xs" px="xs" wrap="wrap">
        <Text size="xs" c="dimmed" fw={500}>Legend:</Text>
        {TASK_STATUS_LEGEND.map(entry => (
          <LegendItem key={entry.label} color={entry.color} label={entry.label} />
        ))}
      </Group>
    </Stack>
  );
}

// ============================================================================
// CUSTOM EVENT RENDERER
// ============================================================================

function renderTaskEvent(eventInfo: EventContentArg) {
  const { isCurrent, status, priority } = eventInfo.event.extendedProps;
  const isHighPriority = priority === 'CRITICAL' || priority === 'HIGH';

  return (
    <Box
      px={4}
      py={2}
      style={{
        fontSize: 11,
        lineHeight: 1.3,
        overflow: 'hidden',
        borderLeft: isCurrent ? '3px solid #1971c2' : undefined,
        paddingLeft: isCurrent ? 4 : undefined,
      }}
    >
      <div style={{ fontWeight: isCurrent ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {isHighPriority && '! '}
        {eventInfo.event.title}
      </div>
      <div style={{ opacity: 0.85, fontSize: 10 }}>
        {eventInfo.timeText}
        {status && ` · ${status.replace(/_/g, ' ').toLowerCase()}`}
      </div>
    </Box>
  );
}

// ============================================================================
// LEGEND ITEM
// ============================================================================

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <Group gap={4}>
      <Box
        w={10}
        h={10}
        style={{
          backgroundColor: color,
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
      <Text size="xs" c="dimmed">{label}</Text>
    </Group>
  );
}
